<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-content1">
      <div class="modal-header">
        <div>
          <h2 class="modal-title">{{ $t('pricing.choosePackage') }}</h2>
          <p class="modal-subtitle">{{ $t('pricing.choosePlan') }}</p>
        </div>
        <button class="close-button" @click="closeModal" :disabled="loading">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
            <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="error-message">
        <i class="fas fa-exclamation-circle"></i>
        <span>{{ error }}</span>
      </div>

      <!-- Success Message -->
      <div v-if="success" class="success-message">
        <i class="fas fa-check-circle"></i>
        <span>{{ $t('pricing.packageSelected') }}</span>
      </div>

      <div class="pricing-grid">
        <!-- Starter Package -->
        <div class="pricing-card"> 
          <div class="package-header">
            <h3 class="package-name">{{ $t('pricing.starter.name') }}</h3>
            <p class="package-description">{{ $t('pricing.starter.description') }}</p>
            <div class="dots-pattern"></div>
          </div>
          <div class="package-price">
            <h2>$0</h2>
            <p class="filing-fee">{{ $t('pricing.filingFee', { fee: 300 }) }}</p>
          </div>
          <div class="benefits-section">
            <h4>{{ $t('pricing.benefits') }}</h4>
            <ul class="benefits-list">
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.starter.benefits.filingTime') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.starter.benefits.accuracy') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.starter.benefits.compliance') }}</span>
              </li>
            </ul>
          </div>
          <button class="package-button" @click.prevent="selectPackage('starter')" :disabled="loading">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <span v-else>{{ $t('pricing.starter.continue') }}</span>
          </button>
        </div>

        <!-- Pro Package -->
        <div class="pricing-card">
          <div class="package-header">
            <h3 class="package-name">{{ $t('pricing.pro.name') }}</h3>
            <p class="package-description">{{ $t('pricing.pro.description') }}</p>
            <div class="dots-pattern"></div>
          </div>
          <div class="package-price">
            <h2>$199</h2>
            <p class="filing-fee">{{ $t('pricing.filingFee', { fee: 300 }) }}</p>
          </div>
          <div class="benefits-section">
            <h4>{{ $t('pricing.benefits') }}</h4>
            <ul class="benefits-list">
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.pro.benefits.filingTime') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.pro.benefits.accuracy') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.pro.benefits.compliance') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.pro.benefits.operatingAgreement') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.pro.benefits.ein') }}</span>
              </li>
            </ul>
          </div>
          <button class="package-button" @click="selectPackage('pro')" :disabled="loading">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <span v-else>{{ $t('pricing.pro.continue') }}</span>
          </button>
        </div>

        <!-- Premium Package -->
        <div class="pricing-card">
          <div class="package-header">
            <h3 class="package-name">{{ $t('pricing.premium.name') }}</h3>
            <p class="package-description">{{ $t('pricing.premium.description') }}</p>
            <div class="dots-pattern"></div>
          </div>
          <div class="package-price">
            <h2>$299</h2>
            <p class="filing-fee">{{ $t('pricing.filingFee', { fee: 300 }) }}</p>
          </div>
          <div class="benefits-section">
            <h4>{{ $t('pricing.benefits') }}</h4>
            <ul class="benefits-list">
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.premium.benefits.proPackage') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.premium.benefits.templates') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.premium.benefits.websiteBuilder') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.premium.benefits.domain') }}</span>
              </li>
              <li>
                <div class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <span>{{ $t('pricing.premium.benefits.email') }}</span>
              </li>
            </ul>
          </div>
          <button class="package-button" @click="selectPackage('premium')" :disabled="loading">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <span v-else>{{ $t('pricing.premium.continue') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@/styles/CustomerBusiness.css'

export default {
  name: 'PricingModal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    success: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    selectPackage(packageType) {
      this.$emit('select', packageType);
    }
  }
}
</script>

<style scoped>
@import '@/styles/CustomerBusiness.css';
</style> 